<template>
  <SubscriptionNavigation
    @submit-clicked="handleSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
    :submit-label="t('subscription.informationsSummary.submitLabel')"
    :is-submit-disabled="!formIsValid"
  >
    <div class="flex flex-col gap-12" data-cy="informations-summary-page">
      <div class="text-center mt-8">
        <AtomText
          :text="t('subscription.informationsSummary.cardTitle')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="informations-summary-page-title"
        />
      </div>

      <SubscriptionSummaryCard />

      <section v-if="hasOptions" data-cy="informations-summary-options">
        <AtomText
          :text="t('subscription.informationsSummary.options.title')"
          class="mb-1"
          font-style="font-semibold"
          font-size="text-lg"
          data-cy="informations-summary-options-title"
        />
        <div class="grid sm:grid-cols-2 gap-4">
          <SummaryOptionCard
            v-if="isBatterySelected"
            :title="batteryOptionTitle"
            :subtitle="batterySubtitle"
            :pictoName="batteryPictoName"
            :tags="batteryTags"
            :edit-step="ESubscriptionRouteName.SOLAR_PRODUCTION_STORAGE"
            data-cy="option-card-battery"
            @edit="handleEditInformation"
          />

          <SummaryOptionCard
            v-if="isPilotageSelected"
            :title="pilotageTitle"
            :subtitle="t('subscription.informationsSummary.options.pilotage.subtitle')"
            pictoName="mySmartPilot"
            :tags="pilotageTags"
            :edit-step="ESubscriptionRouteName.CASHBACK_ACTIVATION"
            data-cy="option-card-pilotage"
            @edit="handleEditInformation"
          />
        </div>
      </section>

      <SummarySection
        :title="t('subscription.informationsSummary.personalInfo.title')"
        data-cy="informations-summary-personal-info"
      >
        <SummaryRow
          :label="t('subscription.informationsSummary.personalInfo.contractor')"
          :edit-step="ESubscriptionRouteName.CONTRACTOR_INFORMATIONS"
          data-cy="summary-row-contractor"
          @edit="handleEditInformation"
        >
          <AtomText :text="contractorName" font-size="text-sm" font-style="font-medium" />
        </SummaryRow>
        <AtomDivider />
        <SummaryRow
          :label="t('subscription.informationsSummary.personalInfo.coordinates')"
          :edit-step="ESubscriptionRouteName.CONTRACTOR_INFORMATIONS"
          data-cy="summary-row-coordinates"
          @edit="handleEditInformation"
        >
          <AtomText :text="coordinates" font-size="text-sm" font-style="font-medium" />
        </SummaryRow>
        <AtomDivider />
        <SummaryRow
          :label="t('subscription.informationsSummary.personalInfo.housingAddress')"
          :edit-step="ESubscriptionRouteName.INSTALLATION_ADDRESS"
          data-cy="summary-row-housing-address"
          @edit="handleEditInformation"
        >
          <span v-if="installationAddressLines">
            <AtomText
              :text="installationAddressLines.line1"
              is-tag="span"
              font-size="text-sm"
              font-style="font-medium"
            />
            <template v-if="installationAddressLines.line1 && installationAddressLines.line2">
              <AtomText text=", " is-tag="span" font-size="text-sm" font-style="font-medium" />
            </template>
            <AtomText
              :text="installationAddressLines.line2"
              is-tag="span"
              font-size="text-sm"
              font-style="font-medium"
            />
          </span>
        </SummaryRow>
      </SummarySection>

      <SummarySection
        :title="t('subscription.informationsSummary.contract.title')"
        data-cy="informations-summary-contract"
      >
        <SummaryRow
          :label="t('subscription.informationsSummary.contract.power')"
          :edit-step="ESubscriptionRouteName.ELECTRICITY_SUPPLIER"
          data-cy="summary-row-power"
          @edit="handleEditInformation"
        >
          <AtomText :text="powerValue" font-size="text-sm" font-style="font-medium" />
        </SummaryRow>
        <AtomDivider />
        <SummaryRow
          :label="t('subscription.informationsSummary.contract.billingMode.title')"
          :description="billingModeDescription"
          :edit-step="ESubscriptionRouteName.BILLING_MODE"
          data-cy="summary-row-billing-mode"
          @edit="handleEditInformation"
        >
          <AtomText :text="billingModeText" font-size="text-sm" font-style="font-medium" />
        </SummaryRow>
        <AtomDivider />
        <SummaryRow
          :label="t('subscription.informationsSummary.contract.billingAddress')"
          :edit-step="ESubscriptionRouteName.BILLING_INFORMATIONS"
          data-cy="summary-row-billing-address"
          @edit="handleEditInformation"
        >
          <span v-if="billingAddressLines">
            <AtomText
              :text="billingAddressLines.line1"
              is-tag="span"
              font-size="text-sm"
              font-style="font-medium"
            />
            <template v-if="billingAddressLines.line1 && billingAddressLines.line2">
              <AtomText text=", " is-tag="span" font-size="text-sm" font-style="font-medium" />
            </template>
            <AtomText
              :text="billingAddressLines.line2"
              is-tag="span"
              font-size="text-sm"
              font-style="font-medium"
            />
          </span>
        </SummaryRow>
        <AtomDivider />
        <SummaryRow
          :label="t('subscription.informationsSummary.contract.iban')"
          :edit-step="ESubscriptionRouteName.BILLING_INFORMATIONS"
          data-cy="summary-row-iban"
          @edit="handleEditInformation"
        >
          <AtomText :text="ibanMasked" font-size="text-sm" font-style="font-medium" />
        </SummaryRow>
      </SummarySection>

      <OrganismAlert
        v-if="isBatterySelected"
        :title="activationFeesTitle"
        :content-text="activationFeesDescription"
        :content-list="activationFeesList"
        :action="{
          actionType: 'externalLink',
          label: isInstalledByInstaller
            ? t('subscription.informationsSummary.alert.activationFees.professionalInstaller.link')
            : t('subscription.informationsSummary.alert.activationFees.selfInstalled.link'),
          link: 'https://help.mylight150.com/fr/articles/662836-frais-d-activation-combien-ca-coute-selon-votre-situation',
        }"
        variant="complex"
        has-icon
        state="info"
        data-cy="informations-summary-monthly-payment-alert"
      />

      <PromoCodeBlock />

      <div>
        <AtomDivider class="mb-6" />
        <div class="flex flex-col gap-4" data-cy="informations-summary-consents">
          <div class="flex items-start gap-3">
            <AtomCheckbox
              id="consent-terms"
              v-model="hasAcceptedTerms"
              class="mt-1 shrink-0"
              data-cy="consent-terms"
            />
            <AtomText is-tag="label" for="consent-terms" class="cursor-pointer">
              <AtomText
                is-tag="span"
                font-size="text-sm"
                :text="tm('subscription.informationsSummary.consents.terms')[0]"
              />&nbsp;<AtomInlineLink
                :label="tm('subscription.informationsSummary.consents.terms')[1]"
                type="url"
                target="blank"
                to="https://www.mylight150.com/pages-legales/conditions-generales"
                font-size="sm"
                font-weight="semibold"
                class="!inline !flex-none"
                data-cy="consent-enedis-terms"
              />&nbsp;<AtomText
                is-tag="span"
                font-size="text-sm"
                :text="tm('subscription.informationsSummary.consents.terms')[2]"
              />
              <AtomText
                is-tag="span"
                font-size="text-sm"
                :text="t('subscription.informationsSummary.consents.mandatoryMarker')"
              />
            </AtomText>
          </div>

          <div class="flex items-start gap-3">
            <AtomCheckbox
              id="consent-enedis"
              v-model="consentEnedis"
              class="mt-1 shrink-0"
              data-cy="consent-enedis"
            />
            <AtomText is-tag="label" for="consent-enedis" class="cursor-pointer">
              <AtomText
                is-tag="span"
                font-size="text-sm"
                :text="t('subscription.informationsSummary.consents.enedis')"
              />
              <AtomText
                is-tag="span"
                font-size="text-sm"
                :text="t('subscription.informationsSummary.consents.mandatoryMarker')"
              />&nbsp;
              <MoleculeStandaloneLink
                :label="t('subscription.informationsSummary.consents.learnMore')"
                type="route"
                font-size="sm"
                font-weight="semibold"
                class="!inline !flex-none"
                data-cy="consent-enedis-learn-more"
                @click.prevent="isEnedisModalOpen = true"
              />
            </AtomText>
          </div>

          <div class="flex items-start gap-3">
            <AtomCheckbox
              id="consent-installer"
              v-model="consentInstaller"
              class="mt-1 shrink-0"
              data-cy="consent-installer"
            />
            <AtomText is-tag="label" for="consent-installer" class="cursor-pointer">
              <AtomText
                is-tag="span"
                font-size="text-sm"
                font-style="font-medium"
                :text="t('subscription.informationsSummary.consents.installerRecommended')"
              /><AtomText
                is-tag="span"
                font-size="text-sm"
                :text="t('subscription.informationsSummary.consents.installer')"
              />&nbsp;<MoleculeStandaloneLink
                :label="t('subscription.informationsSummary.consents.learnMore')"
                type="route"
                font-size="sm"
                font-weight="semibold"
                class="!inline !flex-none"
                data-cy="consent-installer-learn-more"
                @click.prevent="isInstallerModalOpen = true"
              />
            </AtomText>
          </div>

          <AtomText
            :text="t('subscription.informationsSummary.consents.mandatoryFootnote')"
            font-size="text-xs"
            color-variant="description"
            data-cy="informations-summary-mandatory-footnote"
          />
        </div>
      </div>

      <EnedisDataConsentModal v-model:is-open="isEnedisModalOpen" />
      <InstallerDataConsentModal v-model:is-open="isInstallerModalOpen" />
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useEncoreDiscount from '@/composables/useEncoreDiscount.composable';
import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { SubscriptionRouteName } from '@/types/router.type';
import type { SubscriptionAddress } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';
import { formatEuroPrice, formatPhoneNumber, maskIban } from '@/utils/format.util';

import EnedisDataConsentModal from '@/components/subscription/InformationsSummary/EnedisDataConsentModal.component.vue';
import InstallerDataConsentModal from '@/components/subscription/InformationsSummary/InstallerDataConsentModal.component.vue';
import PromoCodeBlock from '@/components/subscription/InformationsSummary/PromoCodeBlock.component.vue';
import SubscriptionSummaryCard from '@/components/subscription/InformationsSummary/SubscriptionSummaryCard.component.vue';
import SummaryOptionCard from '@/components/subscription/InformationsSummary/SummaryOptionCard.component.vue';
import SummaryRow from '@/components/subscription/InformationsSummary/SummaryRow.component.vue';
import SummarySection from '@/components/subscription/InformationsSummary/SummarySection.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { ESubscriptionRouteName } from '@/constants/router.constants';
import {
  AIR_TO_WATER_HEAT_PUMP_ANNUAL_CASHBACK,
  ELECTRIC_WATER_HEATER_ANNUAL_CASHBACK,
  EV_CHARGER_ANNUAL_CASHBACK,
} from '@/constants/subscription.constants';

const { t, tm } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const toastSnackBar = useToastSnackbarStore();

const {
  currentSubscription,
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,

  getCurrentDirectDebit,
  getCurrentSubscriptionOptions,
  getCurrentSubscriptionConsents,
  isBatterySelected,
  isMsbSelected,
  isInstalledByInstaller,
  hasAirToWaterHeatPump,
  hasElectricWaterHeater,
  hasElectricVehicleCharger,
  isPilotageSelected,
} = storeToRefs(subscriptionStore);

const hasAcceptedTerms = ref(false);
const consentEnedis = ref<boolean>(
  getCurrentSubscriptionConsents.value?.consentToEnedisDataSharing ?? false
);
const consentInstaller = ref<boolean>(
  getCurrentSubscriptionConsents.value?.consentToInstallerDataSharing ?? false
);

const isEnedisModalOpen = ref(false);
const isInstallerModalOpen = ref(false);

const individualContractor = computed(
  () => currentSubscription.value?.individualContractor ?? null
);
const companyContractor = computed(() => currentSubscription.value?.companyContractor ?? null);

const contractorName = computed(() => {
  if (companyContractor.value) {
    return companyContractor.value.companyName;
  }

  const contractor = individualContractor.value?.contractor;
  if (!contractor) {
    return '';
  }

  const names = [`${contractor.firstName} ${contractor.lastName}`];
  const coContractor = individualContractor.value?.coContractor;
  if (coContractor) {
    names.push(`${coContractor.firstName} ${coContractor.lastName}`);
  }

  return names.join(', ');
});

const contact = computed(
  () => companyContractor.value?.contact ?? individualContractor.value?.contractor ?? null
);

const coordinates = computed(() =>
  contact.value ? `${contact.value.email} · ${formatPhoneNumber(contact.value.phoneNumber)}` : ''
);

const formatAddressLines = (address: SubscriptionAddress | null | undefined) => {
  if (!address) {
    return null;
  }

  return {
    line1: address.addressComplement
      ? `${address.address}, ${address.addressComplement}`
      : address.address,
    line2: `${address.zipCode} ${address.city}`,
  };
};

const installationAddressLines = computed(() =>
  formatAddressLines(currentSubscription.value?.installationAddress)
);

const billingAddressLines = computed(() =>
  formatAddressLines(currentSubscription.value?.billingInformations?.billingAddress)
);

const energyOffer = computed(() => currentSubscription.value?.energyOfferInformations ?? null);

const powerValue = computed(() => {
  const kVA = energyOffer.value?.contractedPower?.kVA;
  if (kVA == null) {
    return '';
  }

  const pricingOption = energyOffer.value?.energyPricingOption
    ? t(
        `subscription.informationsSummary.contract.pricingOption.${energyOffer.value.energyPricingOption}`
      )
    : '';

  return t('subscription.informationsSummary.contract.powerValue', { kVA, pricingOption });
});

const billingModeText = computed(() => {
  const mode = getCurrentDirectDebit.value?.mode;
  return mode ? t(`subscription.informationsSummary.contract.billingMode.text.${mode}`) : '';
});

const billingModeDescription = computed(() => {
  const mode = getCurrentDirectDebit.value?.mode;
  return mode ? t(`subscription.informationsSummary.contract.billingMode.description.${mode}`) : '';
});

const ibanMasked = computed(() => {
  const iban = currentSubscription.value?.billingInformations?.iban;
  return iban ? maskIban(iban) : '';
});

const hasOptions = computed(() => isBatterySelected.value || isPilotageSelected.value);

const batteryOptionTitle = computed(() => {
  return isMsbSelected.value
    ? t('subscription.informationsSummary.options.battery.mySmartBattery.title')
    : t('subscription.informationsSummary.options.battery.myBattery.title');
});

const batterySubtitle = computed(() => {
  return isMsbSelected.value
    ? t('subscription.informationsSummary.options.battery.mySmartBattery.subtitle')
    : t('subscription.informationsSummary.options.battery.myBattery.subtitle');
});

const batteryPictoName = computed(() => {
  return isMsbSelected.value ? 'mySmartBattery' : 'myBattery';
});

const { encoreDiscount } = useEncoreDiscount();

const batteryTags = computed(() => {
  const tags: Array<{ iconName: string; text: string; type?: 'economy' }> = [];

  if (isMsbSelected.value) {
    const size = getCurrentSubscriptionOptions.value?.battery?.option?.size;
    tags.push({
      iconName: 'ChargingIcon',
      text: t('subscription.informationsSummary.options.battery.mySmartBattery.tagCapacity', {
        size,
      }),
    });
    tags.push({
      iconName: 'MySmartBatteryIcon',
      text: t('subscription.informationsSummary.options.battery.mySmartBattery.tagBattery'),
    });
  } else {
    tags.push({
      iconName: 'ChargingIcon',
      text: t('subscription.informationsSummary.options.battery.myBattery.tagCapacity'),
    });
    tags.push({
      iconName: 'MyBatteryIcon',
      text: t('subscription.informationsSummary.options.battery.myBattery.tagBattery'),
    });
  }

  if (encoreDiscount.value) {
    tags.push({
      iconName: 'CurrencyEuroIcon',
      text: t('subscription.informationsSummary.options.battery.encoreDiscountTag', {
        value: formatEuroPrice(encoreDiscount.value.monthly),
      }),
      type: 'economy',
    });
  }
  return tags;
});

const pilotageTitle = computed(() => {
  let amount = 0;
  if (hasAirToWaterHeatPump.value) {
    amount += AIR_TO_WATER_HEAT_PUMP_ANNUAL_CASHBACK;
  }
  if (hasElectricWaterHeater.value) {
    amount += ELECTRIC_WATER_HEATER_ANNUAL_CASHBACK;
  }
  if (hasElectricVehicleCharger.value) {
    amount += EV_CHARGER_ANNUAL_CASHBACK;
  }
  return t('subscription.informationsSummary.options.pilotage.title', { amount });
});

const pilotageTags = computed(() => {
  const tags = [];
  if (hasAirToWaterHeatPump.value) {
    tags.push({
      iconName: 'CheckIcon',
      text: t('subscription.informationsSummary.options.pilotage.tags.airToWaterHeatPump'),
    });
  }
  if (hasElectricWaterHeater.value) {
    tags.push({
      iconName: 'CheckIcon',
      text: t('subscription.informationsSummary.options.pilotage.tags.electricWaterHeater'),
    });
  }
  if (hasElectricVehicleCharger.value) {
    tags.push({
      iconName: 'CheckIcon',
      text: t('subscription.informationsSummary.options.pilotage.tags.electricVehicleCharger'),
    });
  }
  return tags;
});

const activationFeesTitle = computed(() => {
  return isInstalledByInstaller.value
    ? t('subscription.informationsSummary.alert.activationFees.professionalInstaller.title')
    : t('subscription.informationsSummary.alert.activationFees.selfInstalled.title');
});

const activationFeesDescription = computed(() => {
  return isInstalledByInstaller.value
    ? t('subscription.informationsSummary.alert.activationFees.professionalInstaller.description')
    : t('subscription.informationsSummary.alert.activationFees.selfInstalled.description');
});

const activationFeesList = computed(() => {
  return isInstalledByInstaller.value
    ? [
        tm('subscription.informationsSummary.alert.activationFees.professionalInstaller.list')[0],
        tm('subscription.informationsSummary.alert.activationFees.professionalInstaller.list')[1],
        tm('subscription.informationsSummary.alert.activationFees.professionalInstaller.list')[2],
        tm('subscription.informationsSummary.alert.activationFees.professionalInstaller.list')[3],
      ]
    : undefined;
});

const formIsValid = computed(() => {
  return hasAcceptedTerms.value && consentEnedis.value;
});

const handleEditInformation = (step: SubscriptionRouteName) => {
  stepStore.goToStepById(step);
};

const handleSubmit = async () => {
  if (!formIsValid.value) {
    return;
  }

  try {
    await subscriptionStore.updateSubscriptionConsents({
      consentToEnedisDataSharing: consentEnedis.value,
      consentToInstallerDataSharing: consentInstaller.value,
    });
    await stepStore.goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'informations-summary-error',
      defaultMessage: t('subscription.informationsSummary.api.errors.patchSubscriptionConsents'),
    });
    toastSnackBar.addToast(toastConfig);
  }
};
</script>

<style scoped></style>
